import { ChannelGroupSetting } from 'core/channelGroup/ChannelGroupSettings';
import { L1Object } from 'core/l1Object/L1Object';

/**
 * 「Outdoor 報表下載」的四種報表類型 —— 值就是面板送出時分流用的 key。
 *
 * `LINE_MESSAGE` 的值是 `'beacon'`（不是 `'line_message'`）:那是既有選項的原值，
 * 這次只改它出現的條件，沒有改值。
 */
export const OutdoorReportType = {
  OUTDOOR: 'outdoor',
  OUTDOOR_DEVICE: 'outdoor_device',
  OUTDOOR_DEVICE_BEACON: 'outdoor_device_beacon',
  LINE_MESSAGE: 'beacon'
} as const;

/**
 * 「Outdoor 報表下載」的活動類型判定 —— 下拉選項（DownloadOutdoorReportPanel）與
 * 「下載 Outdoor 報表」按鈕的顯示條件（OrderDetail）共用同一份判定，避免兩邊漂開。
 *
 * 🔴 不可用 `ChannelGroupSetting.isOutdoorChannel()`:它把 `BEACON` 也算成 outdoor
 * （見 ChannelGroupSettings.ts），拿它做二分會把 Beacon Network 判成一般 Outdoor。
 * 要「一般 Outdoor」語意就得明確列 `OUTDOOR` + `MOVABLE_OUTDOOR`，也就是既有的
 * `getOutdoorChannelSettings()`。
 *
 * 用 channelGroupId 比對而不是 `fromId()`:`fromId()` 對未知 id 會 throw，而 l1Object
 * 的 channelGroupId 可能是任何動態 channel group（Beacon Network 的 5 就不在
 * `CHANNEL_GROUP_ID` 常數表裡）。
 */
/** 一般 Outdoor:`OUTDOOR` + `MOVABLE_OUTDOOR`（不含 `BEACON`）。 */
export const hasOutdoorL1Object = (l1ObjectList: L1Object[]): boolean => {
  const ids = ChannelGroupSetting.getOutdoorChannelSettings().map(
    (setting) => setting.config.id
  );
  return l1ObjectList.some((l1Object) => ids.includes(l1Object.channelGroupId));
};

/** Beacon Network（Rocket Beacon Network / MATSUKIYO，channelGroupId 5）。 */
export const hasBeaconNetworkL1Object = (l1ObjectList: L1Object[]): boolean => {
  const ids = ChannelGroupSetting.getBeaconChannelSettings().map(
    (setting) => setting.config.id
  );
  return l1ObjectList.some((l1Object) => ids.includes(l1Object.channelGroupId));
};

/**
 * 「下載 Outdoor 報表」按鈕是否要顯示 —— 有一般 Outdoor 或 Beacon Network 活動才顯示。
 *
 * 判準看的是訂單底下 L1 object 的渠道類型，訂單本身沒有任何欄位能分辨它是不是 outdoor 訂單。
 */
export const shouldShowDownloadOutdoorReport = (
  l1ObjectList: L1Object[]
): boolean =>
  hasOutdoorL1Object(l1ObjectList) || hasBeaconNetworkL1Object(l1ObjectList);
